import axios from 'axios'
import AuthService from './auth.service'
import router from '../router'
import store from '@/store'

// Автоповтор сетевых ошибок (обрыв связи, реконнект VPN, таймаут)
// только для GET/HEAD — иначе повтор может продублировать запись.
const RETRY_METHODS = ['get', 'head']
const MAX_RETRIES = 2
const retryDelay = (attempt) => new Promise((resolve) => setTimeout(resolve, 500 * attempt))

// Request interceptor — добавляем токен и city_id к каждому запросу
axios.interceptors.request.use(
  (config) => {
    const user = AuthService.getCurrentUser()
    if (user && user.access) {
      config.headers.Authorization = `Bearer ${user.access}`
    }

    // Город берём из Vuex-стора, а не из localStorage напрямую: localStorage
    // общий на все вкладки браузера, и при смене города в другой вкладке
    // запросы уходили со старым city_id — шапка показывала один город,
    // а данные приходили другого. Стор у каждой вкладки свой, поэтому
    // «город в шапке» и «город в запросе» всегда совпадают.
    const selectedCityId = store.state.config?.selectedCityId
    if (selectedCityId && !config.params?.city_id) {
      config.params = { ...config.params, city_id: selectedCityId }
    }

    // Общий таймаут: зависший запрос не должен висеть вечно
    // (ERR_NETWORK_CHANGED при смене сети без таймаута оставляет
    // спиннер «Загрузка...» на неопределённое время).
    if (!config.timeout) {
      config.timeout = 30000
    }

    return config
  },
  (error) => Promise.reject(error)
)

// Response interceptor — обрабатываем 401 ошибки
axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true

      try {
        const response = await AuthService.refreshToken()
        if (response.access) {
          originalRequest.headers.Authorization = `Bearer ${response.access}`
          return axios(originalRequest)
        }
        // refresh вернул ответ, но без нового access-токена
        throw new Error('No access token in refresh response')
      } catch {
        AuthService.logout()
        router.push('/login')
        return Promise.reject(error)
      }
    }

    // Повторный 401 после refresh — тоже выходим
    if (error.response?.status === 401) {
      AuthService.logout()
      router.push('/login')
    }

    // Сетевые ошибки без ответа сервера (ERR_NETWORK_CHANGED, ERR_CONNECTION_RESET,
    // таймаут) повторяем — страница прибора должна переживать реконнект VPN.
    // Отменённые запросы (AbortController) не повторяем.
    if (
      !axios.isCancel(error) &&
      !error.response &&
      error.config &&
      RETRY_METHODS.includes((error.config.method || '').toLowerCase())
    ) {
      const attempt = error.config._retryCount || 0
      if (attempt < MAX_RETRIES) {
        error.config._retryCount = attempt + 1
        await retryDelay(attempt + 1)
        return axios(error.config)
      }
    }

    return Promise.reject(error)
  }
)

export default axios
